import React, { useEffect, useRef, useState, useCallback, lazy, Suspense } from 'react';
import { ActionRegistry } from '@packages/table-builder';
import type { ActionIntent } from '@packages/table-builder';

type ModalEntry = {
  Component: React.LazyExoticComponent<React.ComponentType<any>>;
  payload: Record<string, unknown>;
  key: string;
};

const modalLoaders: Record<string, () => Promise<{ default: React.ComponentType<any> }>> = {
  // ── Participant ─────────────────────────────────────────────────
  VIEW_PARTICIPANT: () => import('@apps/customer/domains/participant/ViewParticipant'),
  EDIT_PARTICIPANT: () => import('@apps/customer/domains/participant/EditParticipant'),
  TERMINATE_PARTICIPANT: () => import('@apps/customer/domains/participant/TerminateParticipant'),
  VIEW_TERMINATION_PARTICIPANT: () => import('@apps/customer/domains/participant/TerminateParticipant'),
  VIEW_AUDIT_LOG_DETAIL_PARTICIPANT: () => import('@apps/customer/domains/participant/AuditLogView'),

  // ── Participant Approval ────────────────────────────────────────
  VIEW_PARTICIPANT_APPROVAL: () =>
    import('@apps/customer/domains/participant-approval/ViewParticipantApproval'),

  // ── Participant Broadcast ───────────────────────────────────────
  CREATE_PARTICIPANT_BROADCAST: () =>
    import('@apps/customer/domains/participant-broadcast/ParticipantBroadcastForm'),
  VIEW_PARTICIPANT_BROADCAST: () =>
    import('@apps/customer/domains/participant-broadcast/ParticipantBroadcastForm'),
  EDIT_PARTICIPANT_BROADCAST: () =>
    import('@apps/customer/domains/participant-broadcast/ParticipantBroadcastForm'),

  // ── Securities ──────────────────────────────────────────────────
  VIEW_SECURITIES: () => import('@apps/customer/domains/securities/ViewSecurity'),
  EDIT_SECURITIES: () => import('@apps/customer/domains/securities/EditSecurity'),
  ADD_SECURITIES: () => import('@apps/customer/domains/securities/EditSecurity'),

  // ── Warrants ────────────────────────────────────────────────────
  VIEW_WARRANTS: () => import('@apps/customer/domains/warrants/ViewWarrant'),

  // ── Convertibles ──────────────────────────────────────────────
  VIEW_CONVERTIBLE: () => import('@apps/customer/domains/convertible/ViewConvertible'),

  // ── Charter & Stock Classes ───────────────────────────────────
  VIEW_CHARTER_STOCK_CLASS: () => import('@apps/customer/domains/charter-stock-class/ViewCharterStockClass'),
  FILE_INITIAL_CHARTER: () => import('@apps/customer/domains/charter-stock-class/FileInitialCharter'),
  AMEND_CHARTER: () => import('@apps/customer/domains/charter-stock-class/AmendCharter'),
  EDIT_CHARTER: () => import('@apps/customer/domains/charter-stock-class/EditCharter'),
  IMPORT_CHARTER: () => import('@apps/customer/domains/charter-stock-class/ImportCharter'),

  // ── Valuations ──────────────────────────────────────────────
  VIEW_VALUATION: () => import('@apps/customer/domains/valuation/ViewValuation'),
  EDIT_VALUATION: () => import('@apps/customer/domains/valuation/EditValuation'),
  ADD_VALUATION: () => import('@apps/customer/domains/valuation/EditValuation'),

// ── PSU Awards ──────────────────────────────────────────────
  VIEW_PSU_AWARDS: () => import('@apps/customer/domains/psu-awards/ViewPsuAwardModal'),
  EDIT_PSU_AWARDS: () => import('@apps/customer/domains/psu-awards/EditPsuAwardFormModal'),
  VIEW_COMMENTS_PSU_AWARDS: () => import('@apps/customer/domains/psu-awards/ViewCommentsPsuAwardModal'),

  // ── User-Defined Fields ─────────────────────────────────────
  ADD_USER_DEFINED_FIELDS: () =>
    import('@apps/customer/domains/user-defined-fields/UserDefinedFieldModal'),
  EDIT_USER_DEFINED_FIELDS: () =>
    import('@apps/customer/domains/user-defined-fields/UserDefinedFieldModal'),

  // ── Vendor Terms & Conditions ───────────────────────────────
  ADD_VENDOR_TERMS_CONDITIONS: () =>
    import('@apps/customer/domains/vendor-terms-conditions/VendorTermsConditionsModal'),
  VIEW_VENDOR_TERMS_CONDITIONS: () =>
    import('@apps/customer/domains/vendor-terms-conditions/VendorTermsConditionsModal'),
  EDIT_VENDOR_TERMS_CONDITIONS: () =>
    import('@apps/customer/domains/vendor-terms-conditions/VendorTermsConditionsModal'),

  // ── Grant Management ────────────────────────────────────────────
  VIEW_GRANT_MANAGEMENT: () => import('@apps/customer/domains/grant-management/GrantManagementModal'),
  EDIT_GRANT_MANAGEMENT: () => import('@apps/customer/domains/grant-management/GrantManagementModal'),
  ADD_GRANT_MANAGEMENT: () => import('@apps/customer/domains/grant-management/GrantManagementModal'),

  // ── Grant Acceptance Template ───────────────────────────────────
  ADD_GRANT_ACCEPTANCE_TEMPLATE: () =>
    import('@apps/customer/domains/grant-acceptance-template/GrantAcceptanceTemplateModal'),
  ADD_VERSION_GRANT_ACCEPTANCE_TEMPLATE: () =>
    import('@apps/customer/domains/grant-acceptance-template/GrantAcceptanceTemplateModal'),
  EDIT_CONTENT_GRANT_ACCEPTANCE_TEMPLATE: () =>
    import('@apps/customer/domains/grant-acceptance-template/GrantAcceptanceTemplateModal'),
  COPY_CONTENT_GRANT_ACCEPTANCE_TEMPLATE: () =>
    import('@apps/customer/domains/grant-acceptance-template/GrantAcceptanceTemplateModal'),

  // ── Plan Approval ───────────────────────────────────────────────
  VIEW_PLAN_APPROVAL: () =>
    import('@apps/customer/domains/plan-approval/PlanManagementApprovalModel'),
  VIEW_COMMENTS_PLAN_APPROVAL: () =>
    import('@apps/customer/domains/plan-approval/ViewCommentsPlanApprovalModal'),

  // ── Vesting Templates ───────────────────────────────────────────
  ADD_VESTING_TEMPLATES: () => import('@apps/customer/domains/vesting-templates/VestingTemplateModal'),
  VIEW_VESTING_TEMPLATES: () => import('@apps/customer/domains/vesting-templates/VestingTemplateModal'),
  EDIT_VESTING_TEMPLATES: () => import('@apps/customer/domains/vesting-templates/VestingTemplateModal'),

  // ── Option Awards ─────────────────────────────────────────────
  VIEW_OPTION_AWARDS: () => import('@apps/customer/domains/option-awards/ViewOptionAward'),
  EDIT_OPTION_AWARDS: () => import('@apps/customer/domains/option-awards/EditOptionAward'),
  VIEW_COMMENTS_OPTION_AWARDS: () =>
    import('@apps/customer/domains/option-awards/ViewCommentsOptionAwardModal'),

  // ── Option Award Approval ───────────────────────────────────────
  VIEW_OPTION_AWARD_APPROVAL: () =>
    import('@apps/customer/domains/option-award-approval/ViewOptionAwardApproval'),
  VIEW_COMMENTS_OPTION_AWARD_APPROVAL: () =>
    import('@apps/customer/domains/option-award-approval/ViewCommentsOptionAwardApprovalModal'),

  // ── Exercise ──────────────────────────────────────────────────
  VIEW_DETAILS_EXERCISE: () => import('@apps/customer/domains/exercise/ViewExercise'),
  VIEW_CLOSING_PRICE_EXERCISE: () => import('@apps/customer/domains/exercise/HistoricalStockPrice'),

  // ── RSU Award Approval ────────────────────────────────────────
  VIEW_RSU_AWARD_APPROVAL: () => import('@apps/customer/domains/rsu-award-approval/ViewRsuAwardApproval'),
  VIEW_COMMENTS_RSU_AWARD_APPROVAL: () =>
    import('@apps/customer/domains/rsu-award-approval/ViewCommentsRsuAwardApproval'),
  BULK_PROGRESS_RSU_AWARD_APPROVAL: () =>
    import('@apps/customer/domains/rsu-award-approval/RsuBulkApprovalProgress'),

  // ── PSU Award Approval ────────────────────────────────────────
  VIEW_PSU_AWARD_APPROVAL: () => import('@apps/customer/domains/psu-award-approval/ViewPsuAwardApproval'),
  VIEW_COMMENTS_PSU_AWARD_APPROVAL: () =>
    import('@apps/customer/domains/psu-award-approval/ViewCommentsPsuAwardApproval'),
  BULK_PROGRESS_PSU_AWARD_APPROVAL: () =>
    import('@apps/customer/domains/psu-award-approval/PsuBulkApprovalProgress'),

  // ── Release ───────────────────────────────────────────────────
  RENAME_RELEASE: () => import('@apps/customer/domains/release/RenameReleaseModel'),
  VIEW_DETAILS_RELEASE_INCLUDED: () =>
    import('@apps/customer/domains/release/IncludeItemModel'),
  VIEW_CLOSING_PRICE_RELEASE_INCLUDED: () =>
    import('@apps/customer/domains/release/HistoricalStockPrice'),
  VIEW_DETAILS_RELEASE_DETAILS: () =>
    import('@apps/customer/domains/release/TransactionDetailsModal'),

  // ── Performance Rules ─────────────────────────────────────────
  ADD_PERFORMANCE_RULE: () => import('@apps/customer/domains/performance-rule/PerformanceRulesModal'),
  VIEW_PERFORMANCE_RULE: () => import('@apps/customer/domains/performance-rule/PerformanceRulesModal'),
  EDIT_PERFORMANCE_RULE: () => import('@apps/customer/domains/performance-rule/PerformanceRulesModal'),
  VIEW_COMMENTS_PERFORMANCE_RULE: () =>
    import('@apps/customer/domains/performance-rule/ViewCommentsPerformanceRule'),

  // ── Performance Rule Approval ─────────────────────────────────
  VIEW_PERFORMANCE_RULE_APPROVAL: () =>
    import('@apps/customer/domains/performance-rule-approval/PerformanceRulesApprovalModal'),
  VIEW_COMMENTS_PERFORMANCE_RULE_APPROVAL: () =>
    import('@apps/customer/domains/performance-rule-approval/ViewCommentsPerformanceRuleApproval'),

  // ── Share Pool ────────────────────────────────────────────────
  ADD_SHARE_POOL: () => import('@apps/customer/domains/share-pool/SharePoolModal'),
  VIEW_SHARE_POOL: () => import('@apps/customer/domains/share-pool/SharePoolModal'),
  EDIT_SHARE_POOL: () => import('@apps/customer/domains/share-pool/SharePoolModal'),

  // ── Transfer & Allocate ───────────────────────────────────────
  RENAME_TRANSFER_ALLOCATE: () =>
    import('@apps/customer/domains/transfer-and-allocate/RenameTransferAllocateModel'),
  VIEW_DETAILS_TRANSFER_ALLOCATE_ITEM: () =>
    import('@apps/customer/domains/transfer-and-allocate/IncludedModal'),
  VIEW_CLOSING_PRICE_TRANSFER_ALLOCATE: () =>
        import('@apps/customer/domains/transfer-and-allocate/HistoricalStockPrice'),

    // ── Exercisability Tier Configuration ───────────────────────────
    ADD_EXERCISABILITY_TIER_CONFIG: () =>
        import('@apps/customer/domains/exercisability-tier-config/CreateExercisabilityTierConfigModal'),
    VIEW_EXERCISABILITY_TIER_CONFIG: () =>
        import('@apps/customer/domains/exercisability-tier-config/ViewExercisabilityTierConfig'),
    EDIT_EXERCISABILITY_TIER_CONFIG: () =>
        import('@apps/customer/domains/exercisability-tier-config/EditExercisabilityTierConfigModal'),

    // ── Exercisability Instructions ─────────────────────────────────
    ADD_EXERCISABILITY_INSTRUCTIONS: () =>
        import('@apps/customer/domains/exercisability-instructions/CreateExercisabilityInstructionsModal'),
    VIEW_EXERCISABILITY_INSTRUCTIONS: () =>
        import('@apps/customer/domains/exercisability-instructions/ViewExercisabilityInstructions'),
    EDIT_EXERCISABILITY_INSTRUCTIONS: () =>
        import('@apps/customer/domains/exercisability-instructions/EditExercisabilityInstructionsModal'),

    // ── SAR Awards ──────────────────────────────────────────────────
    VIEW_SAR_AWARD: () => import('@apps/customer/domains/sar-awards/ViewSarAward'),
    IMPORT_SAR_AWARD: () =>
        import('@apps/customer/domains/sar-awards/components/SarAwardImportModal'),

    // ── SAR Award Approvals ─────────────────────────────────────────
    VIEW_SAR_AWARD_APPROVAL_OVERVIEW: () =>
        import('@apps/customer/domains/sar-approvals/ViewSarAwardApprovalOverview'),
    VIEW_SAR_AWARD_APPROVAL_VESTING: () =>
        import('@apps/customer/domains/sar-approvals/ViewSarAwardApprovalVesting'),
    VIEW_SAR_AWARD_APPROVAL_DOCUMENTS: () =>
        import('@apps/customer/domains/sar-approvals/ViewSarAwardApprovalDocuments'),

    // ── SAR My Holdings (Participant) ───────────────────────────────
    VIEW_SAR_HOLDING: () => import('@apps/customer/domains/sar-my-holdings/ViewSarHoldingDetail'),

    // ── SAR Exercises ───────────────────────────────────────────────
    VIEW_SAR_EXERCISE_DETAIL: () =>
        import('@apps/customer/domains/sar-exercises/ViewSarExerciseDetail'),
    UPDATE_SAR_EXERCISE_FMV: () =>
        import('@apps/customer/domains/sar-exercises/UpdateSarExerciseFmvModal'),
    UPDATE_SAR_EXERCISE_TAXES: () =>
        import('@apps/customer/domains/sar-exercises/UpdateSarExerciseTaxesModal'),
    IMPORT_SAR_EXERCISE: () =>
        import('@apps/customer/domains/sar-exercises/ImportSarExerciseModal'),

    // ── ESPP ──────────────────────────────────────────────────────
    VIEW_OFFERING_PERIOD_ESPP: () => import('@apps/customer/domains/espp/EsppOfferingPeriodModal'),

    // ── Transactions — Purchase Details ───────────────────────────
    VIEW_TRANSACTIONS_PURCHASEDETAILS: () =>
      import('@apps/customer/domains/transactions-purchase-details/TransactionDetailsModal'),

};

let intentCounter = 0;

/**
 * Mediator component — subscribes to ActionRegistry intents and lazy-loads
 * the appropriate modal. Mounted once in CustomerAppShell.
 *
 * Two-phase close: sets `isOpen` to false (triggers CSS exit transition),
 * then unmounts via `onExitComplete` from the modal — the modal owns its
 * animation lifecycle, not this handler.
 */
export function ActionIntentHandler() {
  const [activeModal, setActiveModal] = useState<ModalEntry | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const lastIntentRef = useRef<{ actionId: string; ts: number } | null>(null);

  const handleIntent = useCallback((intent: ActionIntent) => {
    if (intent.type !== 'OPEN_MODAL') return;

    const now = Date.now();
    const last = lastIntentRef.current;
    if (last && last.actionId === intent.actionId && now - last.ts < 500) {
      return;
    }
    lastIntentRef.current = { actionId: intent.actionId, ts: now };

    const loader = modalLoaders[intent.actionId];
    if (!loader) {
      console.warn(`[ActionIntentHandler] No modal loader for "${intent.actionId}"`);
      return;
    }

    const Component = lazy(loader);
    intentCounter += 1;

    setActiveModal({
      Component,
      payload: intent.payload,
      key: `${intent.actionId}-${intentCounter}`,
    });
    setIsOpen(true);
  }, []);

  useEffect(() => {
    return ActionRegistry.onIntent(handleIntent);
  }, [handleIntent]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleExitComplete = useCallback(() => {
    setActiveModal(null);
  }, []);

  if (!activeModal) return null;

  const { Component, payload, key } = activeModal;

  return (
    <Suspense fallback={null}>
      <Component
        key={key}
        {...payload}
        onClose={handleClose}
        onExitComplete={handleExitComplete}
        isOpen={isOpen}
      />
    </Suspense>
  );
}
